/**
 * AdmissionsFeeSettings — what the school charges an applicant, and whether
 * they can pay it by card instead of waiting on the bursar.
 */
import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api, { getErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Loader2, CreditCard, Smartphone } from 'lucide-react';

const PaynowSettings = ({ tenantId }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(null);

  const { data } = useQuery({
    queryKey: ['paynow-settings', tenantId],
    queryFn: async () => (await api.get(
      `/school/admissions/tenants/${tenantId}/paynow-settings`)).data,
  });

  useEffect(() => {
    if (data && !form) {
      setForm({
        integration_id: data.integration_id || '',
        integration_key: '',
        merchant_email: data.merchant_email || '',
        enabled_methods: data.enabled_methods || [],
        enabled: !!data.enabled,
      });
    }
  }, [data, form]);

  const save = useMutation({
    mutationFn: async (body) => (await api.put(
      `/school/admissions/tenants/${tenantId}/paynow-settings`, body)).data,
    onSuccess: () => {
      toast.success('Mobile money settings saved');
      setForm((p) => ({ ...p, integration_key: '' }));
      queryClient.invalidateQueries({ queryKey: ['paynow-settings', tenantId] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (!form) return null;
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const toggle = (key) => setForm((p) => ({
    ...p,
    enabled_methods: p.enabled_methods.includes(key)
      ? p.enabled_methods.filter((x) => x !== key)
      : [...p.enabled_methods, key],
  }));

  return (
    <Card data-testid="paynow-settings">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-sm font-semibold">Mobile money — EcoCash, OneMoney, InnBucks</CardTitle>
            <p className="text-xs text-slate-500 mt-0.5">
              Your own Paynow merchant details (paynow.co.zw → Receive Payment Links → 3rd Party Site).
              The key is stored once and never shown again.
            </p>
          </div>
          <Badge variant="outline" className={data?.enabled && data?.integration_key_set
            ? 'text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200'
            : 'text-[10px]'} data-testid="paynow-status-badge">
            <Smartphone className="h-3 w-3 mr-1" />
            {data?.enabled && data?.integration_key_set ? 'Mobile money on' : 'Not configured'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Paynow Integration ID</Label>
            <Input value={form.integration_id} onChange={set('integration_id')}
                   data-testid="paynow-id-input" placeholder="e.g. 12345" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Integration Key</Label>
            <Input value={form.integration_key} onChange={set('integration_key')}
                   data-testid="paynow-key-input" type="password"
                   placeholder={data?.integration_key_set
                     ? `saved ${data.integration_key_hint} — type to replace`
                     : 'paste the key'} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Merchant email</Label>
            <Input value={form.merchant_email} onChange={set('merchant_email')}
                   data-testid="paynow-email-input"
                   placeholder="the email on the Paynow account" />
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {(data?.wallets || []).map((w) => {
            const on = form.enabled_methods.includes(w.key);
            return (
              <button key={w.key} type="button" onClick={() => toggle(w.key)}
                      data-testid={`paynow-wallet-${w.key}`}
                      className={`rounded-full px-4 py-1.5 text-xs font-semibold border transition-colors ${
                        on ? 'bg-slate-900 text-white border-slate-900'
                           : 'bg-white text-slate-500 border-slate-200 hover:border-slate-400'}`}>
                {w.label}
              </button>
            );
          })}
        </div>

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={form.enabled} data-testid="paynow-enabled-toggle"
                   onChange={(e) => setForm((p) => ({ ...p, enabled: e.target.checked }))} />
            Let applicants pay from their phone
          </label>
          <Button onClick={() => save.mutate(form)} disabled={save.isPending}
                  data-testid="paynow-save-btn">
            {save.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export const AdmissionsFeeSettings = ({ tenantId }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(null);

  const { data } = useQuery({
    queryKey: ['admissions-finance-settings', tenantId],
    queryFn: async () => (await api.get(
      `/school/admissions/tenants/${tenantId}/finance-settings`)).data,
  });

  useEffect(() => {
    if (data && !form) {
      setForm({
        application_fee: data.application_fee ?? 0,
        registration_deposit: data.registration_deposit ?? 0,
        currency: data.currency || 'USD',
      });
    }
  }, [data, form]);

  const save = useMutation({
    mutationFn: async (body) => (await api.put(
      `/school/admissions/tenants/${tenantId}/finance-settings`, {
        application_fee: Number(body.application_fee) || 0,
        registration_deposit: Number(body.registration_deposit) || 0,
        currency: (body.currency || 'USD').toUpperCase(),
      })).data,
    onSuccess: () => {
      toast.success('Fees saved');
      queryClient.invalidateQueries({ queryKey: ['admissions-finance-settings', tenantId] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (!form) return null;
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  return (
    <Card data-testid="admissions-fee-settings">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle className="text-sm font-semibold">Application fees</CardTitle>
          <Badge variant="outline" className={data?.online_payments
            ? 'text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200'
            : 'text-[10px]'} data-testid="fee-online-badge">
            <CreditCard className="h-3 w-3 mr-1" />
            {data?.online_payments ? 'Card payments on' : 'Card payments off'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
          <div className="space-y-1.5">
            <Label className="text-xs">Application fee</Label>
            <Input value={form.application_fee} onChange={set('application_fee')}
                   data-testid="fee-application-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Registration deposit</Label>
            <Input value={form.registration_deposit} onChange={set('registration_deposit')}
                   data-testid="fee-deposit-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Currency</Label>
            <Input value={form.currency} onChange={set('currency')}
                   data-testid="fee-currency-input" />
          </div>
          <Button onClick={() => save.mutate(form)} disabled={save.isPending}
                  data-testid="fee-save-btn">
            {save.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save
          </Button>
        </div>
        <p className="mt-3 text-xs text-slate-500">
          With a fee set, applicants can pay by card or from their phone, and the receipt lands in
          the same ledger the bursar reads.
        </p>
      </CardContent>
    </Card>
  );
};

export const AdmissionsPaymentSettings = ({ tenantId }) => (
  <div className="space-y-4">
    <AdmissionsFeeSettings tenantId={tenantId} />
    <PaynowSettings tenantId={tenantId} />
  </div>
);

export default AdmissionsFeeSettings;
